
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useMemo, useState } from "react";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

import ImpactFundingOverview from "./ImpactFundingOverview";
import { columnAnchorDomId } from "../utils/impactPages";



import {
  HOPE_SANS,
  HOPE_SERIF,
} from "@/shared/branding/thehopeonebrand";

import type {
  ImpactCardData,
} from "../utils/impactCardData";

import {
  formatImpactMoney,
} from "../utils/impactCurrency";
import { resolveImpactFundingTotals } from "../utils/impactFundingTotals";

/* -------------------------------------------------------------------------- */
/* Theme                                                                      */
/* -------------------------------------------------------------------------- */
const ADMIN_FUNDING_OVERVIEW_COLUMN_ID = "admin-funding-overview";
const c = {
  forestDeep: "#102C24",
  forest: "#1D4437",
  green: "#2F6B4F",

  sage: "#E7F0EA",
  mist: "#F3F6F3",

  paper: "#FFFDF8",
  white: "#FFFFFF",

  muted: "#5F6F68",
  soft: "#8A968F",
  ink: "#16221C",

  line: "rgba(16,44,36,.08)",
  border: "rgba(16,44,36,.12)",

  error: "#9B3A3A",
  errorBg: "#FCEEEE",

  gold: "#8A6B2E",
  goldBg: "#FAF1E1",
};

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

type Status =
  | "Committed"
  | "Pending"
  | "Verified"
  | "Rejected";

  type VerifyInput = {
    contributionId: string | number;
    status: "Verified" | "Rejected";
    verifiedAmount?: number;
    note?: string;
  };

type ImpactAdminContributorsPageProps = {
  data: ImpactCardData;

  verifyingId?: string | number | null;

  onVerify?: (
    input: VerifyInput
  ) => Promise<void> | void;
};

type Contributor =
  ImpactCardData["contributors"][number];

type FilterType =
  | "all"
  | "review"
  | "Verified";

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const money = (amount: number) =>
  formatImpactMoney(amount);

/** Column captions that only appear once the table reflows on phones. */
const rowCellLabelSx = {
  display: { xs: "block", sm: "none" },
  fontFamily: HOPE_SANS,
  fontSize: "0.58rem",
  fontWeight: 800,
  letterSpacing: "0.08em",
  textTransform: "uppercase" as const,
  color: c.soft,
} as const;

const needsReview = (status: Status) =>
  status === "Committed" ||
  status === "Pending";

const getStatusConfig = (status: Status) => {
  switch (status) {
    case "Verified":
      return {
        label: "입금 확인",
        color: c.green,
        background: c.sage,
      };

    case "Rejected":
      return {
        label: "반려",
        color: c.error,
        background: c.errorBg,
      };

    case "Pending":
      return {
        label: "확인 대기",
        color: c.gold,
        background: c.goldBg,
      };

    default:
      return {
        label: "기부 약속",
        color: c.gold,
        background: c.goldBg,
      };
  }
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function ImpactAdminContributorsPage({
  data,
  verifyingId,
  onVerify,
}: ImpactAdminContributorsPageProps) {

  /* ---------------------------------------------------------------------- */
  /* State                                                                  */
  /* ---------------------------------------------------------------------- */

  const [query, setQuery] = useState("");

  const [filter, setFilter] =
    useState<FilterType>("all");

  const [selected, setSelected] =
    useState<Contributor | null>(null);

  const [action, setAction] =
    useState<"verify" | "reject" | null>(null);

  const [submitting, setSubmitting] =
    useState(false);

  const [error, setError] =
    useState("");

  /* ---------------------------------------------------------------------- */
  /* Contribution data                                                      */
  /* ---------------------------------------------------------------------- */

  const people = Array.isArray(data?.contributors)
    ? data.contributors
    : [];

  const campaignFunding = useMemo(
    () => resolveImpactFundingTotals(data),
    [data]
  );

  const reviewCount = people.filter(
    (item) => needsReview(item.status)
  ).length;

  /* ---------------------------------------------------------------------- */
  /* Search and filtering                                                   */
  /* ---------------------------------------------------------------------- */

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return people.filter((item) => {
      if (
        filter === "review" &&
        !needsReview(item.status)
      ) {
        return false;
      }

      if (
        filter === "Verified" &&
        item.status !== "Verified"
      ) {
        return false;
      }

      if (!needle) {
        return true;
      }

      return (
        item.name.toLowerCase().includes(needle) ||
        String(item.amount).includes(needle) ||
        String(item.id).includes(needle)
      );
    });
  }, [people, filter, query]);

  /* ---------------------------------------------------------------------- */
  /* Verification actions                                                   */
  /* ---------------------------------------------------------------------- */

  const openVerify = (item: Contributor) => {
    if (!onVerify) return;

    setSelected(item);
    setAction("verify");
    setError("");
  };

  const openReject = (item: Contributor) => {
    if (!onVerify) return;

    setSelected(item);
    setAction("reject");
    setError("");
  };

  const closeDialog = () => {
    if (submitting) return;

    setSelected(null);
    setAction(null);
    setError("");
  };

  const confirmAction = async () => {
    if (!selected || !action || !onVerify) {
      return;
    }
  
    if (!needsReview(selected.status)) {
      setError(
        "이 기부 내역은 더 이상 확인 대기 상태가 아닙니다."
      );
      return;
    }
  
    const contributionId = selected.id;
  
    setError("");
  
    try {
      setSubmitting(true);
  
      if (action === "verify") {
        await onVerify({
          contributionId,
          status: "Verified",
          verifiedAmount: selected.amount,
        });
      } else {
        await onVerify({
          contributionId,
          status: "Rejected",
        });
      }
  
      setSelected(null);
      setAction(null);
    } catch {
      setError(
        "기부 내역을 업데이트하지 못했습니다. 다시 시도해 주세요."
      );
    } finally {
      setSubmitting(false);
    }
  };
  /* ---------------------------------------------------------------------- */
  /* UI                                                                     */
  /* ---------------------------------------------------------------------- */

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        overflowX: "hidden",
        fontFamily: HOPE_SANS,
        color: c.ink,
      }}
    >
      {/* SUBPAGE 1 — CONTRIBUTOR REVIEW */}
      <Box
        component="section"
        sx={{
          width: "100%",
          minWidth: 0,
          minHeight: "100cqh",
          boxSizing: "border-box",
          scrollSnapAlign: "start",
          scrollSnapStop: "always",
          pb: 2,
        }}
      >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: HOPE_SERIF,
              fontWeight: 700,
              fontSize: {
                xs: "1.5rem",
                sm: "1.85rem",
              },
              color: c.forestDeep,
              letterSpacing: "-0.025em",
            }}
          >
            기부자 현황 관리
          </Typography>

          <Typography
            sx={{
              mt: 0.5,
              fontFamily: HOPE_SANS,
              fontSize: "0.78rem",
              color: c.muted,
              lineHeight: 1.5,
            }}
          >
            기부 약속 내역을 검토하고 실제 입금된 금액을 확인합니다.
          </Typography>
        </Box>

     
      </Stack>

      {/* ================================================================ */}
      {/* Search                                                            */}
      {/* ================================================================ */}

      <TextField
        fullWidth
        size="small"
        value={query}
        onChange={(event) =>
          setQuery(event.target.value)
        }
        placeholder="기부자 이름, 금액 또는 내역 번호 검색..."
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRoundedIcon
                sx={{
                  fontSize: 19,
                  color: c.soft,
                }}
              />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 1.5,

          "& .MuiOutlinedInput-root": {
            borderRadius: "11px",
            bgcolor: c.white,
            fontFamily: HOPE_SANS,
            minHeight: 42,
          },
        }}
      />

      {/* ================================================================ */}
      {/* Filters                                                           */}
      {/* ================================================================ */}

      <Stack
        direction="row"
        spacing={0.75}
        sx={{
          mb: 1.75,
          overflowX: "auto",
          pb: 0.5,
        }}
      >
        {[
          {
            key: "all",
            label: `전체 (${people.length})`,
          },

          {
            key: "review",
            label: `확인 대기 (${reviewCount})`,
          },

          {
            key: "Verified",
            label: "입금 확인",
          },
        ].map((item) => {
          const active = filter === item.key;

          return (
            <Button
              key={item.key}
              size="small"
              onClick={() =>
                setFilter(
                  item.key as FilterType
                )
              }
              sx={{
                textTransform: "none",

                borderRadius: "999px",

                px: 1.5,

                minHeight: 30,

                flexShrink: 0,

                fontFamily: HOPE_SANS,

                fontSize: "0.72rem",

                fontWeight: 700,

                bgcolor: active
                  ? c.forestDeep
                  : c.white,

                color: active
                  ? "#FFFFFF"
                  : c.forest,

                border: `1px solid ${
                  active
                    ? c.forestDeep
                    : c.border
                }`,

                "&:hover": {
                  bgcolor: active
                    ? c.forest
                    : c.sage,
                },
              }}
            >
              {item.label}
            </Button>
          );
        })}
      </Stack>

      {/* ================================================================ */}
      {/* Contribution table                                               */}
      {/* ================================================================ */}

      <Box
        sx={{
          borderRadius: "14px",

          border: `1px solid ${c.border}`,

          overflow: "hidden",

          bgcolor: c.white,
        }}
      >
        <Box
          sx={{
            width: "100%",
            /* Phones reflow each row into a stacked block, so only the wider
               table layout needs a horizontal scroller. */
            overflowX: { xs: "hidden", sm: "auto" },
          }}
        >
          <Box sx={{ minWidth: { xs: 0, sm: 0 } }}>

            {/* Table header */}

            <Box
              sx={{
                display: { xs: "none", sm: "grid" },

                gridTemplateColumns:
                  "minmax(140px, 1.5fr) minmax(180px, 1.2fr) 90px",

                gap: 1,

                px: 1.5,
                py: 1.25,

                bgcolor: c.mist,

                borderBottom:
                  `1px solid ${c.border}`,
              }}
            >
              {[
                "기부자",
                "기부 약속 금액",
                "상태",
              ].map((label) => (
                <Typography
                  key={label}
                  sx={{
                    fontFamily: HOPE_SANS,

                    fontSize: "0.61rem",

                    fontWeight: 800,

                    letterSpacing: "0.06em",

                    color: c.muted,
                  }}
                >
                  {label}
                </Typography>
              ))}
            </Box>

            {/* Table rows */}

            {rows.map((row, index) => {
              const id = String(row.id);

              const busy =
                String(verifyingId) === id ||
                (submitting &&
                  String(selected?.id) === id);

              const awaiting =
                needsReview(row.status);

              const status =
                getStatusConfig(row.status);

              return (
                <Box
                  key={id}
                  sx={{
                    display: { xs: "flex", sm: "grid" },

                    flexWrap: "wrap",

                    gridTemplateColumns:
                      "minmax(140px, 1.5fr) minmax(180px, 1.2fr) 90px",

                    gap: 1,

                    px: 1.5,
                    py: 1.4,

                    alignItems: "center",

                    borderTop:
                      index === 0
                        ? "none"
                        : `1px solid ${c.line}`,

                    "&:hover": {
                      bgcolor: "#FAFCFA",
                    },
                  }}
                >

                  {/* Contributor */}

                  <Box sx={{ minWidth: 0, flexBasis: { xs: "100%", sm: "auto" } }}>
                    <Typography
                      noWrap
                      sx={{
                        fontFamily: HOPE_SANS,

                        fontWeight: 800,

                        fontSize: "0.78rem",

                        color: c.forestDeep,
                      }}
                    >
                      {row.name}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.25,

                        fontFamily: HOPE_SANS,

                        fontSize: "0.65rem",

                        color: c.soft,
                      }}
                    >
                      {new Date(
                        row.contributedAt
                      ).toLocaleDateString("ko-KR")}
                    </Typography>
                  </Box>

                  {/* 기부 약속 금액 + verify in one box */}

                  <Box
                    sx={{
                      minWidth: 0,
                      flex: { xs: 1, sm: "unset" },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      px: 1,
                      py: 0.7,
                      borderRadius: "10px",
                      bgcolor: awaiting ? c.mist : row.status === "Verified" ? c.sage : c.mist,
                      border: `1px solid ${c.line}`,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={rowCellLabelSx}>기부 약속 금액</Typography>
                      <Typography
                        sx={{
                          fontFamily: HOPE_SANS,

                          fontWeight: 700,

                          fontSize: "0.77rem",

                          color: c.ink,

                          fontVariantNumeric:
                            "tabular-nums",

                          overflowWrap: "anywhere",
                        }}
                      >
                        {money(row.amount)}
                      </Typography>
                    </Box>

                    <Stack
                      direction="row"
                      spacing={0.25}
                      alignItems="center"
                      sx={{ flexShrink: 0 }}
                    >
                      {awaiting ? (
                        <>
                          <IconButton
                            size="small"
                            disabled={
                              busy ||
                              !onVerify
                            }
                            onClick={() =>
                              openVerify(row)
                            }
                            aria-label="입금 확인"
                            title="기부 약속 금액의 입금 확인"
                            sx={{
                              width: 28,
                              height: 28,

                              bgcolor:
                                c.forestDeep,

                              color:
                                "#FFFFFF",

                              "&:hover": {
                                bgcolor:
                                  c.forest,
                              },

                              "&.Mui-disabled": {
                                opacity: 0.4,
                              },
                            }}
                          >
                            <CheckRoundedIcon
                              sx={{
                                fontSize: 17,
                              }}
                            />
                          </IconButton>

                          <IconButton
                            size="small"
                            disabled={
                              busy ||
                              !onVerify
                            }
                            onClick={() =>
                              openReject(row)
                            }
                            aria-label="기부 내역 반려"
                            title="기부 내역 반려"
                            sx={{
                              width: 28,
                              height: 28,

                              color:
                                c.error,
                            }}
                          >
                            <CloseRoundedIcon
                              sx={{
                                fontSize: 17,
                              }}
                            />
                          </IconButton>
                        </>
                      ) : (
                        <CheckRoundedIcon
                          sx={{
                            fontSize: 18,
                            color:
                              row.status === "Verified"
                                ? c.green
                                : c.soft,
                          }}
                        />
                      )}
                    </Stack>
                  </Box>

                  {/* Status */}

                  <Chip
                    label={status.label}
                    size="small"
                    sx={{
                      height: 23,

                      bgcolor:
                        status.background,

                      color:
                        status.color,

                      fontFamily:
                        HOPE_SANS,

                      fontWeight: 700,

                      fontSize: "0.65rem",
                    }}
                  />
                </Box>
              );
            })}

            {/* Empty state */}

            {rows.length === 0 && (
              <Box
                sx={{
                  px: 2,
                  py: 4,
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontFamily:
                      HOPE_SANS,

                    fontWeight: 700,

                    color: c.forest,
                  }}
                >
                  기부 내역이 없습니다
                </Typography>

                <Typography
                  sx={{
                    mt: 0.5,

                    fontFamily:
                      HOPE_SANS,

                    fontSize: "0.75rem",

                    color: c.muted,
                  }}
                >
                  검색어나 필터를 변경해 보세요.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Table footer */}

        <Box
          sx={{
            px: 1.5,
            py: 1.1,

            bgcolor: c.mist,

            borderTop:
              `1px solid ${c.border}`,
          }}
        >
          <Typography
            sx={{
              fontFamily: HOPE_SANS,

              fontSize: "0.68rem",

              color: c.muted,
            }}
          >
            전체 {people.length}건 중 {rows.length}건 표시
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          pt: 2,
          pb: 1,
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily: HOPE_SANS,
            fontSize: "0.7rem",
            fontWeight: 700,
            color: c.muted,
          }}
        >
          아래로 스크롤 · 모금 현황
        </Typography>
        <KeyboardArrowDownRoundedIcon
          sx={{ mt: 0.25, fontSize: 28, color: c.green }}
        />
      </Box>
      </Box>

      {/* SUBPAGE 2 — FUNDING OVERVIEW */}
      <Box
        component="section"
        id={columnAnchorDomId(ADMIN_FUNDING_OVERVIEW_COLUMN_ID)}
        sx={{
          width: "100%",
          minWidth: 0,
          minHeight: "100cqh",
          boxSizing: "border-box",
          px: { xs: 0.25, sm: 0 },
          py: { xs: 2.5, sm: 3 },
          bgcolor: c.paper,
          borderTop: `1px solid ${c.border}`,
          scrollSnapAlign: "start",
          scrollSnapStop: "always",
        }}
      >
        <Typography
          sx={{
            fontFamily: HOPE_SERIF,
            fontWeight: 700,
            fontSize: { xs: "1.35rem", sm: "1.55rem" },
            color: c.forestDeep,
            mb: 0.5,
          }}
        >
          모금 현황
        </Typography>
        <Typography
          sx={{
            mb: 2,
            fontFamily: HOPE_SANS,
            fontSize: "0.78rem",
            lineHeight: 1.55,
            color: c.muted,
          }}
        >
          캠페인 목표 대비 약속·인증 금액과 참여 현황을 확인합니다.
        </Typography>
        <ImpactFundingOverview
          goalAmount={campaignFunding.goalAmount}
          committedAmount={campaignFunding.committedAmount}
          verifiedAmount={campaignFunding.verifiedAmount}
          contributorCount={campaignFunding.contributorCount}
        />
      </Box>

      {/* ================================================================ */}
      {/* Verification confirmation dialog                                 */}
      {/* ================================================================ */}

      <Dialog
        open={Boolean(selected && action)}
        onClose={closeDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "18px",
            p: 0.5,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: HOPE_SERIF,

            fontWeight: 700,

            fontSize: "1.45rem",

            color: c.forestDeep,
          }}
        >
          {action === "verify"
            ? "입금 확인"
            : "기부 내역 반려"}
        </DialogTitle>

        <DialogContent>
          {selected && (
            <Stack spacing={2}>
              <Typography
                sx={{
                  fontFamily: HOPE_SANS,

                  fontSize: "0.82rem",

                  color: c.muted,

                  lineHeight: 1.6,
                }}
              >
                {action === "verify"
                  ? "카드 생성자의 은행 계좌에 실제로 입금된 금액인지 확인한 후 승인해 주세요."
                  : "이 기부 내역을 반려하시겠습니까?"}
              </Typography>

              {/* Contribution details */}

              <Box
                sx={{
                  p: 2,

                  bgcolor: c.mist,

                  borderRadius: "12px",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: HOPE_SANS,

                    fontWeight: 800,

                    color: c.forestDeep,
                  }}
                >
                  {selected.name}
                </Typography>

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mt: 1 }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: c.muted,
                    }}
                  >
                    기부 약속 금액
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: HOPE_SERIF,
                      fontSize: "1.05rem",
                      fontWeight: 700,
                      color: c.forestDeep,
                    }}
                  >
                    {money(selected.amount)}
                  </Typography>
                </Stack>
              </Box>

              {error && (
                <Typography
                  sx={{
                    fontFamily: HOPE_SANS,

                    fontSize: "0.75rem",

                    color: c.error,
                  }}
                >
                  {error}
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            pb: 2,
            pt: 0,
          }}
        >
          <Button
            onClick={closeDialog}
            disabled={submitting}
            sx={{
              textTransform: "none",
              color: c.muted,
            }}
          >
            취소
          </Button>

          <Button
            variant="contained"
            onClick={() =>
              void confirmAction()
            }
            disabled={
              submitting ||
              !onVerify
            }
            sx={{
              textTransform: "none",

              borderRadius: "10px",

              px: 2,

              bgcolor:
                action === "verify"
                  ? c.forestDeep
                  : c.error,

              "&:hover": {
                bgcolor:
                  action === "verify"
                    ? c.forest
                    : "#842E2E",
              },
            }}
          >
            {submitting
              ? "처리 중..."
              : action === "verify"
              ? "입금 확인 완료"
              : "기부 내역 반려"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}